/**
 * The authenticated application shell + route gate.
 *
 * `<RequireSession>` enforces a live session — bouncing to the external login
 * site on a miss and showing the loop-guard fallback if a redirect loop trips.
 * Inside it, {@link SessionScopedProviders} supplies flags/tenant/permissions
 * from the session claims, then the sidebar + header frame the routed `Outlet`.
 *
 * `<DebugResourceProvider>` sits between the routed content and `<DebugPanel>`
 * so a page can publish diagnostics about the RECORD it is showing (`?debug=1`)
 * and the panel grows a tab for it. Most resources need no page code at all —
 * the API client captures whatever the backend published — so the provider is
 * given the pathname to clear those captures on navigation, which is what stops
 * the previous page's record lingering in the panel. Mounted unconditionally
 * and inert with debug off; the shell never learns which routes publish.
 */
import * as React from "react";
import { AlertTriangle } from "lucide-react";
import { Outlet, useRouterState } from "@tanstack/react-router";
import { Button } from "@authe/ui-core";
import { RequireSession, useSession } from "@authe/ui-core/session";
import { DebugResourceProvider } from "@authe/ui-core/debug";
import { AskAiProvider } from "./AskAiProvider";
import { DisplayPrefsIdentity, SessionScopedProviders } from "./providers";
import { DocumentTitleProvider } from "./DocumentTitleProvider";
import { PostSetupTokenGuard } from "./PostSetupTokenGuard";
import { Sidebar } from "@/components/shell/Sidebar";
import { Header } from "@/components/shell/Header";
import { DebugPanel } from "@/components/DebugPanel";
import { APP_NAME, DASHBOARD_ERRORS } from "@/config";
import { DashboardEntryTelemetry } from "./useActivationTelemetry";
import { MAIN_CONTENT_ID } from "./layoutIds";
import { DashboardAddressDisplayProvider } from "@/features/overlays/DashboardAddressDisplayProvider";
import { scrollKeyOf, useScrollRestoration } from "./scrollRestoration";

function SigningIn(): React.ReactElement {
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-3 bg-background text-foreground">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-primary" />
      <p className="text-sm text-muted-foreground">Signing in to {APP_NAME}…</p>
    </div>
  );
}

function AuthNotice({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="flex h-screen items-center justify-center bg-background p-6">
      <div className="flex max-w-sm flex-col items-center text-center">
        <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
          <AlertTriangle className="h-6 w-6" />
        </div>
        <p className="text-sm font-semibold text-foreground">{title}</p>
        <p className="mt-1 text-sm text-muted-foreground">{description}</p>
        {action}
      </div>
    </div>
  );
}

/**
 * The circuit breaker tripped — sign-in really is bouncing. No retry button:
 * another attempt would just extend the loop.
 */
function AuthBlocked(): React.ReactElement {
  return <AuthNotice {...DASHBOARD_ERRORS.SESSION_LOOP} />;
}

/**
 * A terminal auth failure, which today means a *refused* sign-in — the
 * post-login Action denying (e.g. a removed org). An expired/stale session is
 * not terminal: the session manager clears the dead token cache and the guard
 * below redirects to login, so it never reaches this screen. Auth0's own deny
 * reason is the useful copy when we have it.
 */
function AuthError(): React.ReactElement {
  const { state, login } = useSession();
  return (
    <AuthNotice
      title={DASHBOARD_ERRORS.SESSION_ENDED.title}
      description={state.error?.message || DASHBOARD_ERRORS.SESSION_ENDED.description}
      action={
        <Button size="sm" className="mt-4" onClick={() => void login()}>
          Sign in again
        </Button>
      }
    />
  );
}

/**
 * Where `<main>` starts on each arrival: top on a new page, WHERE YOU LEFT IT on
 * Back or Forward, and untouched when only the search params moved.
 *
 * The shell scrolls in `<main>`, not the window, so neither the browser nor the
 * router's own scroll restoration (which targets the window) does anything here.
 * This hook is the shell's own, and the three cases it decides between live in
 * `scrollRestoration.ts` as a pure function — the reason each one exists is
 * written there.
 *
 * The offset is recorded on the way OUT, from a ref rather than from React
 * state: by the time an effect for the new location runs, `<main>` has already
 * been re-rendered with the new page and its `scrollTop` is no longer the one
 * being left.
 *
 * StrictMode double-invokes effects on MOUNT only, never on a dependency
 * change — so the only doubled run is the first paint, which is a "top" with no
 * pending restore to cancel. The second pass sees the entry it just recorded and
 * reads as "stay". Nothing to guard; noted so nobody adds a guard that breaks it.
 */
function useMainScrollRestoration(ref: React.RefObject<HTMLElement | null>): void {
  const location = useRouterState({ select: (s) => s.location });
  useScrollRestoration(ref, { key: scrollKeyOf(location), scope: location.pathname });
}

export function RootLayout(): React.ReactElement {
  const mainRef = React.useRef<HTMLElement>(null);
  useMainScrollRestoration(mainRef);
  // Path only, for the same reason the scroll reset uses it: switching a tab or
  // a chain selector rewrites the search params on the SAME record, and
  // dropping the debug captures there would blank the panel mid-inspection.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // The phone-width navigation drawer (the rail itself is hidden below 768px).
  const [navOpen, setNavOpen] = React.useState(false);
  const openNav = React.useCallback(() => setNavOpen(true), []);
  return (
    <RequireSession loading={<SigningIn />} blocked={<AuthBlocked />} error={<AuthError />}>
      {/*
        Self-heal a stale/bootstrap-only token from the setup hand-off before the
        app shell paints: one hard refresh, else bounce to /setup. Sits ABOVE the
        session-scoped layers so we never render the dashboard with setup-only
        scopes. See PostSetupTokenGuard.
      */}
      <PostSetupTokenGuard>
        <SessionScopedProviders>
          {/* Behaviours, not layout: the first binds the preference keyspace to
              the signed-in analyst, the second keeps the tab's name in step with
              the route and whatever the page is about. */}
          <DisplayPrefsIdentity />
          <DashboardEntryTelemetry />
          <DocumentTitleProvider>
          <AskAiProvider>
            <DashboardAddressDisplayProvider>
            <DebugResourceProvider routeKey={pathname}>
              {/* Print: the shell is a fixed-height frame whose <main> scrolls,
                  so printing any page used to capture one screen of it — with
                  the sidebar and header — and cut the rest. On paper the frame
                  unwinds to the page's full height and the chrome goes; the
                  `!` beats the inline height below. The case report
                  (`/investigations/$id/report`) is the page this exists for, but
                  every page prints better for it. */}
              <div className="flex h-screen overflow-hidden bg-background print:block print:h-auto! print:overflow-visible" style={{ height: "calc(100dvh - var(--impersonation-banner-height, 0px) - var(--demo-banner-height, 0px))" }}>
                <div className="contents print:hidden">
                  <Sidebar mobileOpen={navOpen} onMobileOpenChange={setNavOpen} />
                </div>
                <div className="flex min-w-0 flex-1 flex-col print:block">
                  <div className="contents print:hidden">
                    <Header onOpenNav={openNav} />
                  </div>
                  {/* `id` is the skip link's target: the sidebar is 26 entries
                      deep, so reaching the page by keyboard without one means
                      tabbing all of them on every navigation. */}
                  <main id={MAIN_CONTENT_ID} ref={mainRef} className="flex-1 overflow-y-auto p-4 md:p-6 print:overflow-visible print:p-0">
                    <Outlet />
                  </main>
                </div>
              </div>
              <div className="contents print:hidden">
                <DebugPanel />
              </div>
            </DebugResourceProvider>
            </DashboardAddressDisplayProvider>
          </AskAiProvider>
          </DocumentTitleProvider>
        </SessionScopedProviders>
      </PostSetupTokenGuard>
    </RequireSession>
  );
}
